/**
 * /superadmin/pass/guias — CRUD de guías locales.
 *
 * Contenido gratuito (playas, miradores, museos, mercados) que los afiliados
 * recomiendan junto a sus ofertas comerciales.
 */

import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { toast } from 'sonner';
import {
  Loader2, Plus, BookOpen, Edit3, Trash2, Save, X, MapPin, Image as ImageIcon, Upload, Eye, EyeOff, ExternalLink, CheckCircle2,
} from 'lucide-react';
import { passAdmin } from '../../services/passApi';
import { uploadProductImage } from '../../services/api';

const CATEGORIES = [
  ['general', 'General'],
  ['playa', 'Playa'],
  ['mirador', 'Mirador'],
  ['mercado', 'Mercado'],
  ['museo', 'Museo'],
  ['parque', 'Parque'],
  ['monumento', 'Monumento'],
  ['evento', 'Evento'],
  ['gastronomia', 'Gastronomía'],
  ['ruta', 'Ruta'],
  ['transporte', 'Transporte'],
];

const STATUS = [
  ['active', 'Activa'],
  ['draft', 'Borrador'],
  ['archived', 'Archivada'],
];

const EMPTY = {
  title: '',
  summary: '',
  body: '',
  cover_image_url: '',
  category: 'general',
  city: 'Puerto Vallarta',
  address: '',
  latitude: '',
  longitude: '',
  tags: '',
  status: 'active',
  order: 0,
  custom_slug: '',
};

export default function PassGuides() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | guide_id
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const res = await passAdmin.listGuides();
      setRows(res.data.guides || []);
    } catch (err) {
      toast.error('Error cargando guías');
    } finally {
      setLoading(false);
    }
  }

  function openNew() {
    setForm(EMPTY);
    setEditing('new');
  }

  async function openEdit(id) {
    try {
      const res = await passAdmin.getGuide(id);
      const g = res.data.guide;
      setForm({
        title: g.title || '',
        summary: g.summary || '',
        body: g.body || '',
        cover_image_url: g.cover_image_url || '',
        category: g.category || 'general',
        city: g.city || 'Puerto Vallarta',
        address: g.address || '',
        latitude: g.latitude ?? '',
        longitude: g.longitude ?? '',
        tags: (g.tags || []).join(', '),
        status: g.status || 'active',
        order: g.order || 0,
        custom_slug: g.slug || '',
      });
      setEditing(id);
    } catch (err) {
      toast.error('No se pudo cargar la guía');
    }
  }

  function close() {
    setEditing(null);
    setForm(EMPTY);
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadProductImage(file);
      setForm((f) => ({ ...f, cover_image_url: res.data.image_url }));
      toast.success('Imagen subida');
    } catch (err) {
      toast.error('Error subiendo imagen');
    } finally {
      setUploading(false);
    }
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        summary: form.summary || null,
        body: form.body || null,
        cover_image_url: form.cover_image_url || null,
        category: form.category,
        city: form.city,
        address: form.address || null,
        latitude: form.latitude !== '' ? Number(form.latitude) : null,
        longitude: form.longitude !== '' ? Number(form.longitude) : null,
        tags: (form.tags || '').split(',').map((t) => t.trim()).filter(Boolean),
        status: form.status,
        order: Number(form.order) || 0,
      };
      if (editing === 'new') {
        if (form.custom_slug && form.custom_slug.trim()) payload.custom_slug = form.custom_slug.trim();
        await passAdmin.createGuide(payload);
        toast.success('Guía creada');
      } else {
        await passAdmin.updateGuide(editing, payload);
        toast.success('Guía actualizada');
      }
      close();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('¿Eliminar esta guía? Esta acción no se puede deshacer.')) return;
    try {
      await passAdmin.deleteGuide(id);
      toast.success('Guía eliminada');
      await load();
    } catch (err) {
      toast.error('Error eliminando');
    }
  }

  async function toggleStatus(g) {
    const next = g.status === 'active' ? 'draft' : 'active';
    try {
      await passAdmin.updateGuide(g.id, { status: next });
      toast.success(next === 'active' ? 'Publicada' : 'Despublicada');
      await load();
    } catch (err) {
      toast.error('Error');
    }
  }

  return (
    <DashboardLayout>
      <div className="px-6 py-6">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
          <h1 className="text-2xl font-bold text-slate-900 inline-flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-teal-600" /> Guías locales — Pass
          </h1>
          <button
            onClick={openNew}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold"
          >
            <Plus className="w-4 h-4" /> Nueva guía
          </button>
        </div>
        <p className="text-sm text-slate-500 mb-5">
          Contenido gratuito que los afiliados pueden mostrar junto a sus ofertas. No genera comisión.
        </p>

        {editing && (
          <GuideEditor
            form={form}
            setForm={setForm}
            saving={saving}
            uploading={uploading}
            onUpload={handleUpload}
            onSave={handleSave}
            onClose={close}
            isNew={editing === 'new'}
          />
        )}

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : rows.length === 0 ? (
            <p className="text-center text-slate-500 py-10">Sin guías. Crea la primera con "Nueva guía".</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">Título</th>
                  <th className="text-left px-4 py-2">Categoría</th>
                  <th className="text-left px-4 py-2">Ciudad</th>
                  <th className="text-left px-4 py-2">Estado</th>
                  <th className="text-right px-4 py-2">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {rows.map((g) => (
                  <tr key={g.id}>
                    <td className="px-4 py-2">
                      <div className="font-semibold text-slate-900">{g.title}</div>
                      <div className="text-xs text-slate-500 font-mono">/pass/guia/{g.slug}</div>
                    </td>
                    <td className="px-4 py-2 text-xs">{CATEGORIES.find(([v]) => v === g.category)?.[1] || g.category}</td>
                    <td className="px-4 py-2 text-xs">{g.city}</td>
                    <td className="px-4 py-2">
                      <StatusBadge status={g.status} />
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex gap-1">
                        <IconBtn title="Ver pública" onClick={() => window.open(`/pass/guia/${g.slug}`, '_blank')} icon={ExternalLink} />
                        <IconBtn title="Editar" onClick={() => openEdit(g.id)} icon={Edit3} />
                        <IconBtn
                          title={g.status === 'active' ? 'Despublicar' : 'Publicar'}
                          onClick={() => toggleStatus(g)}
                          icon={g.status === 'active' ? EyeOff : Eye}
                          color={g.status === 'active' ? 'amber' : 'green'}
                        />
                        <IconBtn title="Eliminar" onClick={() => handleDelete(g.id)} icon={Trash2} color="red" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function GuideEditor({ form, setForm, saving, uploading, onUpload, onSave, onClose, isNew }) {
  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }
  return (
    <div className="bg-white border-2 border-teal-500 rounded-xl p-5 mb-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold text-slate-900 inline-flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-teal-600" /> {isNew ? 'Nueva guía' : 'Editar guía'}
        </h2>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
          <X className="w-5 h-5" />
        </button>
      </div>

      <form onSubmit={onSave} className="space-y-4">
        {/* Cover image */}
        <div>
          <label className="text-xs font-semibold text-slate-700">Imagen de portada</label>
          {form.cover_image_url ? (
            <div className="mt-1 relative">
              <img src={form.cover_image_url} alt="" className="w-full h-40 object-cover rounded-lg border border-slate-200" />
              <button
                type="button"
                onClick={() => update('cover_image_url', '')}
                className="absolute top-2 right-2 px-2 py-1 rounded-lg bg-white/90 text-red-600 text-xs font-semibold border border-slate-200"
              >
                Quitar
              </button>
            </div>
          ) : (
            <label className="mt-1 cursor-pointer flex items-center justify-center gap-2 px-4 py-6 border-2 border-dashed border-slate-300 rounded-lg text-slate-500 text-sm hover:bg-slate-50">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? 'Subiendo...' : 'Subir imagen'}
              <input type="file" accept="image/*" onChange={onUpload} className="hidden" disabled={uploading} />
            </label>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Título *" required>
            <input
              required value={form.title}
              onChange={(e) => update('title', e.target.value)}
              placeholder="Ej. Playas imperdibles de Vallarta"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
            />
          </Field>
          {isNew && (
            <Field label="Slug personalizado (opcional)">
              <input
                value={form.custom_slug}
                onChange={(e) => update('custom_slug', e.target.value)}
                placeholder="playas-imperdibles-vallarta"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono"
              />
            </Field>
          )}
        </div>

        <Field label="Resumen corto">
          <input
            value={form.summary}
            onChange={(e) => update('summary', e.target.value)}
            placeholder="1-2 líneas que aparecen en la card. Ej. Las 5 playas más bonitas y cómo llegar."
            maxLength={300}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
          />
        </Field>

        <Field label="Contenido (markdown)">
          <textarea
            value={form.body}
            onChange={(e) => update('body', e.target.value)}
            placeholder="# Las playas imperdibles&#10;&#10;## 1. Playa Conchas Chinas&#10;..."
            rows={10}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono"
          />
          <p className="text-[11px] text-slate-500 mt-1">
            Markdown soportado: # encabezados, **negrita**, *cursiva*, [link](url), saltos de línea, listas con -.
          </p>
        </Field>

        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Categoría">
            <select
              value={form.category}
              onChange={(e) => update('category', e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
            >
              {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <Field label="Ciudad">
            <input
              value={form.city}
              onChange={(e) => update('city', e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Estado">
            <select
              value={form.status}
              onChange={(e) => update('status', e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
            >
              {STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Tags (separados por coma)">
          <input
            value={form.tags}
            onChange={(e) => update('tags', e.target.value)}
            placeholder="familiar, gratis, atardecer"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
          />
        </Field>

        <div className="pt-2 border-t border-slate-200">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 inline-flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5" /> Ubicación (opcional, para botón "Cómo llegar")
          </h4>
          <Field label="Dirección o nombre del lugar">
            <input
              value={form.address}
              onChange={(e) => update('address', e.target.value)}
              placeholder="Playa Conchas Chinas, Puerto Vallarta, Jalisco"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
            />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Latitud">
              <input
                type="number" step="any"
                value={form.latitude}
                onChange={(e) => update('latitude', e.target.value)}
                placeholder="20.5907"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Longitud">
              <input
                type="number" step="any"
                value={form.longitude}
                onChange={(e) => update('longitude', e.target.value)}
                placeholder="-105.2363"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Orden (menor = arriba)">
              <input
                type="number"
                value={form.order}
                onChange={(e) => update('order', e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
              />
            </Field>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">
            Si no tienes coordenadas exactas, deja vacío y el mapa usará la dirección.
          </p>
        </div>

        <div className="flex gap-2 pt-3 border-t border-slate-200">
          <button
            type="submit" disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isNew ? 'Crear guía' : 'Guardar cambios'}
          </button>
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm">
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-slate-700">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function StatusBadge({ status }) {
  const map = {
    active:   { label: 'Activa', cls: 'bg-green-50 text-green-700', icon: CheckCircle2 },
    draft:    { label: 'Borrador', cls: 'bg-blue-50 text-blue-700', icon: ImageIcon },
    archived: { label: 'Archivada', cls: 'bg-slate-100 text-slate-600', icon: EyeOff },
  };
  const m = map[status] || { label: status, cls: 'bg-slate-100 text-slate-700', icon: ImageIcon };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${m.cls}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

function IconBtn({ title, onClick, icon: Icon, color }) {
  const colors = {
    red:   'text-red-600 hover:bg-red-50',
    amber: 'text-amber-600 hover:bg-amber-50',
    green: 'text-green-600 hover:bg-green-50',
  };
  return (
    <button
      type="button" title={title} onClick={onClick}
      className={`p-1.5 rounded-lg ${colors[color] || 'text-slate-600 hover:bg-slate-100'}`}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
}
